import { useState, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Trash2, Plus, ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useWorkspaceContext } from "@/hooks/useWorkspaceContext";
import { ROUTES } from "@/lib/constants";
import type { AgentRun } from "@/lib/agent/types";

interface SessionListProps {
  runs: AgentRun[];
  activeRunId: string | null;
  collapsed: boolean;
  taskCounts?: Map<string, number>;
  onSelect: (runId: string) => void;
  onDelete: (runId: string) => void;
  onNewChat: () => void;
}

const ITEMS_PER_SECTION = 5;

/** Group sessions by relative date */
function groupByDate(runs: AgentRun[], t: (key: string) => string) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000);

  const groups: { label: string; runs: AgentRun[] }[] = [
    { label: t("agent.dateToday"), runs: [] },
    { label: t("agent.dateYesterday"), runs: [] },
    { label: t("agent.datePrevious7Days"), runs: [] },
    { label: t("agent.dateOlder"), runs: [] },
  ];

  const [gToday, gYesterday, gWeek, gOlder] = groups;

  for (const run of runs) {
    const d = new Date(run.createdAt);
    if (d >= today) gToday!.runs.push(run);
    else if (d >= yesterday) gYesterday!.runs.push(run);
    else if (d >= weekAgo) gWeek!.runs.push(run);
    else gOlder!.runs.push(run);
  }

  return groups.filter((g) => g.runs.length > 0);
}

// ─── Collapsible section wrapper ─────────────────────────

function SectionHeader({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted hover:text-text-secondary transition-colors"
    >
      {collapsed ? (
        <ChevronRight className="h-3 w-3 shrink-0" />
      ) : (
        <ChevronDown className="h-3 w-3 shrink-0" />
      )}
      <span className="flex-1 truncate">{label}</span>
      <span className="text-text-muted">{count}</span>
    </button>
  );
}

// ─── Show more / less toggle ─────────────────────────────

function ShowMoreToggle({
  total,
  showingAll,
  onToggle,
}: {
  total: number;
  showingAll: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const remaining = total - ITEMS_PER_SECTION;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center px-3 py-1.5 text-left text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
    >
      {showingAll
        ? t("agent.showLess", "Show less")
        : t("agent.seeAll", "See all ({{count}} more)", { count: remaining })}
    </button>
  );
}

// ─── Main Component ──────────────────────────────────────

export function SessionList({
  runs,
  activeRunId,
  collapsed,
  taskCounts,
  onSelect,
  onDelete,
  onNewChat,
}: SessionListProps) {
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  // Workspace chats are listed in /chats; this list is assistant runs only.
  // A workspace url can still be open, so picking a run returns to solo.
  const { activeQuestId } = useWorkspaceContext();
  const navigate = useNavigate();

  // Switching conversations is real navigation now, so each click that does
  // not actually change the url would still push a history entry. Back would
  // then pop entries that restore nothing visible — which run is selected is
  // store state, not part of the /assistant url — and the user would press it
  // repeatedly with nothing happening. Navigate only when the route differs.
  const goToSolo = useCallback(() => {
    if (activeQuestId) navigate(ROUTES.ASSISTANT);
  }, [activeQuestId, navigate]);

  const handleConversationClick = (runId: string) => {
    goToSolo();
    onSelect(runId);
  };

  const handleNewChat = () => {
    goToSolo();
    onNewChat();
  };

  const toggleSectionCollapsed = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSectionExpanded = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderSection = (key: string, label: string, total: number, items: ReactNode[]) => {
    const sectionCollapsed = collapsedSections.has(key);
    const showAll = expandedSections.has(key);
    const visible = showAll ? items : items.slice(0, ITEMS_PER_SECTION);
    const hasMore = total > ITEMS_PER_SECTION;

    return (
      <div key={key}>
        <SectionHeader
          label={label}
          count={total}
          collapsed={sectionCollapsed}
          onToggle={() => toggleSectionCollapsed(key)}
        />
        {!sectionCollapsed && (
          <>
            {visible}
            {hasMore && (
              <ShowMoreToggle
                total={total}
                showingAll={showAll}
                onToggle={() => toggleSectionExpanded(key)}
              />
            )}
          </>
        )}
      </div>
    );
  };

  if (collapsed) {
    return (
      <div className="mt-2 flex flex-col items-center gap-1 px-2">
        <button
          onClick={handleNewChat}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:bg-surface-secondary hover:text-text-primary transition-colors"
          title={t("agent.newChat")}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const groups = groupByDate(runs, t);

  const renderConversation = (run: AgentRun) => (
    <div
      key={run.id}
      className={cn(
        "group relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors",
        run.id === activeRunId && !activeQuestId
          ? "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
          : "text-text-secondary hover:bg-surface-secondary hover:text-text-primary",
      )}
      onClick={() => handleConversationClick(run.id)}
      onMouseEnter={() => setHoveredId(run.id)}
      onMouseLeave={() => setHoveredId(null)}
    >
      <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-50" />

      <span className="flex-1 truncate">
        {run.title || run.prompt.slice(0, 50)}
      </span>

      {(taskCounts?.get(run.id) ?? 0) > 0 && (
        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold text-white">
          {taskCounts!.get(run.id)}
        </span>
      )}

      {hoveredId === run.id && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(run.id);
          }}
          className="shrink-0 rounded p-0.5 text-text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          title={t("agent.deleteSession")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="mt-4 flex h-full flex-col px-2">
      {/* New chat button — pinned, not scrollable */}
      <button
        onClick={handleNewChat}
        className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-secondary hover:text-text-primary transition-colors mb-1"
      >
        <Plus className="h-4 w-4 shrink-0" />
        <span>{t("agent.newChat")}</span>
      </button>

      {/* Scrollable section list */}
      <div className="flex flex-1 flex-col gap-1 overflow-y-auto pb-4">
        {/* Grouped session list */}
        {groups.map((group) =>
          renderSection(
            `runs:${group.label}`,
            group.label,
            group.runs.length,
            group.runs.map(renderConversation),
          ),
        )}

        {runs.length === 0 && (
          <p className="px-3 py-4 text-center text-xs text-text-muted">
            {t("agent.noSessions")}
          </p>
        )}
      </div>
    </div>
  );
}
